import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { DemoOverviewTab } from '@/components/demo/DemoOverviewTab';
import { DemoSensitivityTab } from '@/components/demo/DemoSensitivityTab';
import {
  Building2,
  FileText,
  BarChart3,
  Activity,
  ArrowLeft,
  RefreshCw,
  Zap,
  Download,
  FileDown,
  Target,
  Calculator,
  Globe,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

export default function DemoExperience() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sensitivityResult, setSensitivityResult] = useState(null);

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
      return;
    }
    fetchDemoData();
  }, [user, token, navigate]);

  const fetchDemoData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demo/analysis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDemoData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateDemo = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/demo/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setDemoData(data.analysis);
        toast.success('Demo experience loaded!');
      }
    } catch (error) {
      toast.error('Error generating demo');
    } finally {
      setGenerating(false);
    }
  };

  const exportPDF = async () => {
    if (!demoData?.id) return;
    toast.info('Generating PDF...');
    try {
      const response = await fetch(`${API_URL}/api/demo/export/pdf/${demoData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = 'SunRise_Agro_Credit_Memo.pdf';
        a.click();
        toast.success('PDF downloaded!');
      }
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!demoData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <img src={LOGO_URL} alt="Apaka" className="h-16 mx-auto mb-4" />
            <CardTitle className="text-3xl font-heading font-black">Experience Apaka in Action</CardTitle>
            <CardDescription className="text-lg mt-2">
              See how our AI-powered platform transforms credit decisions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Building2, title: 'Full Company Profile', desc: 'Nigerian agri-exporter' },
                { icon: BarChart3, title: 'AI Risk Analysis', desc: 'BBB rating assessment' },
                { icon: Activity, title: 'Sensitivity Testing', desc: 'Interactive scenarios' },
                { icon: FileText, title: 'Credit Memo', desc: 'IC-ready export' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                  <item.icon className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={generateDemo} disabled={generating} className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700" data-testid="generate-demo-btn">
              {generating ? <><RefreshCw className="h-5 w-5 mr-2 animate-spin" />Generating...</> : <><Zap className="h-5 w-5 mr-2" />Launch Interactive Demo</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, loan_request, analysis_result, sensitivity_analysis } = demoData;
  const baseCase = sensitivity_analysis?.base_case || {};
  const currentResult = sensitivityResult?.outputs || baseCase;
  const recommendation = sensitivityResult?.recommendation || {
    decision: analysis_result.decision,
    recommended_amount: analysis_result.structure_recommendation.recommended_amount,
    recommended_spread_bps: analysis_result.structure_recommendation.pricing.spread_bps,
    all_in_rate: analysis_result.structure_recommendation.pricing.all_in_rate
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-5 w-5" /><span className="hidden sm:inline">Back</span>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <img src={LOGO_URL} alt="Apaka" className="h-10" />
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Demo Mode</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportPDF}><FileDown className="h-4 w-4 mr-2" />Export</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-6 border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-blue-200 text-sm mb-1">Loan Application Analysis</p>
                <h1 className="text-2xl md:text-3xl font-heading font-bold">{company.company_name}</h1>
                <p className="text-blue-100 mt-1">{company.sector} • {company.country}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-lg">
                  <p className="text-xs text-blue-200">Requested</p>
                  <p className="text-xl font-bold">{formatCurrency(loan_request.loan_amount)}</p>
                </div>
                <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-lg">
                  <p className="text-xs text-blue-200">Risk Grade</p>
                  <p className="text-xl font-bold">{currentResult.risk_grade || analysis_result.risk_grade}</p>
                </div>
                <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-lg">
                  <p className="text-xs text-blue-200">Decision</p>
                  <p className="text-xl font-bold">{recommendation.decision?.replace(/_/g, ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Target className="h-4 w-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="sensitivity" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Calculator className="h-4 w-4 mr-2" />Sensitivity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <DemoOverviewTab demoData={demoData} currentResult={currentResult} recommendation={recommendation} />
          </TabsContent>

          <TabsContent value="sensitivity">
            <DemoSensitivityTab token={token} baseCase={baseCase} onResultChange={setSensitivityResult} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
